import React, { useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import {
  Eye, RefreshCw, TrendingUp, TrendingDown, Minus, Activity,
  Gauge, Layers, Crosshair, Radar, ScrollText, CheckCircle2, XCircle,
  ShieldCheck, Sparkles, AlertTriangle, Search, MessageSquareQuote,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

/* ── helpers ──────────────────────────────────────────────────────────── */

const biasKind = (b) => {
  const v = String(b || "").toUpperCase();
  if (v.startsWith("BULL")) return "bull";
  if (v.startsWith("BEAR")) return "bear";
  return "neutral";
};

const BIAS_STYLE = {
  bull:    { tint: "#34d399", label: "BULLISH", Icon: TrendingUp },
  bear:    { tint: "#fb7185", label: "BEARISH", Icon: TrendingDown },
  neutral: { tint: "#a1a1aa", label: "NEUTRAL", Icon: Minus },
};

const fmtPrice = (p) => {
  const n = Number(p);
  if (!n) return "—";
  if (n >= 1000) return n.toLocaleString(undefined, { maximumFractionDigits: 2 });
  if (n >= 1)    return n.toFixed(3);
  return n.toPrecision(4);
};

const fmtTime = (iso) => {
  if (!iso) return "never";
  const d = new Date(iso);
  if (isNaN(d)) return "—";
  const mins = Math.round((Date.now() - d.getTime()) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.round(hrs / 24)}d ago`;
};

const cleanSym = (s) => String(s || "").replace(/[-/]?USDT.*$/i, "").replace(/USDT$/i, "") || s;

const scoreBand = (s) => {
  const n = Number(s) || 0;
  return n >= 71 ? "STRONG" : n >= 51 ? "MODERATE" : n >= 31 ? "WEAK" : "NEUTRAL";
};

// Historical hit-rate for a given score+bias, from the calibration table.
const hitRateFor = (calib, score, bias) => {
  if (!calib?.bands) return null;
  const band = calib.bands[scoreBand(score)];
  if (!band) return null;
  const k = biasKind(bias);
  const side = k === "bull" ? "BULL" : k === "bear" ? "BEAR" : "combined";
  const v = band[side]?.hit;
  return v == null ? band.combined?.hit ?? null : v;
};

// Color a hit-rate: >52 green, 48–52 amber, <48 rose. 50% = coin-flip.
const hitTint = (h) => (h == null ? "#a1a1aa" : h >= 52 ? "#34d399" : h >= 48 ? "#fbbf24" : "#fb7185");

/* ── small presentational pieces ──────────────────────────────────────── */

function BiasBadge({ bias }) {
  const k = biasKind(bias);
  const { tint, label, Icon } = BIAS_STYLE[k];
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide"
      style={{ color: tint, background: `${tint}18`, boxShadow: `inset 0 0 0 1px ${tint}44` }}
    >
      <Icon className="h-3 w-3" /> {label}
    </span>
  );
}

function ScoreDial({ score, tint }) {
  const pct = Math.max(0, Math.min(100, Number(score) || 0));
  const R = 20, C = 2 * Math.PI * R;
  return (
    <div className="relative h-[52px] w-[52px] shrink-0">
      <svg viewBox="0 0 52 52" className="h-full w-full -rotate-90">
        <circle cx="26" cy="26" r={R} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="4" />
        <circle
          cx="26" cy="26" r={R} fill="none" stroke={tint} strokeWidth="4" strokeLinecap="round"
          strokeDasharray={C} strokeDashoffset={C * (1 - pct / 100)}
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-[13px] font-bold text-white">{Math.round(pct)}</span>
      </div>
    </div>
  );
}

function AlignDots({ tf }) {
  const cells = [["1h", tf?.["1h"]], ["4h", tf?.["4h"]], ["1d", tf?.["1d"]]];
  return (
    <div className="flex items-center gap-1.5">
      {cells.map(([k, v]) => {
        const kind = biasKind(v);
        const tint = BIAS_STYLE[kind].tint;
        return (
          <span key={k} className="flex items-center gap-1 text-[9px] text-white/40">
            <span className="h-1.5 w-1.5 rounded-full" style={{ background: tint }} />
            {k}
          </span>
        );
      })}
    </div>
  );
}

function LayerBar({ label, data }) {
  const score = Number(data?.score || 0);
  const max = Number(data?.max || 0) || 1;
  const pct = Math.max(0, Math.min(100, (score / max) * 100));
  return (
    <div>
      <div className="flex items-center justify-between text-[10px] mb-1">
        <span className="capitalize text-white/55">{label.replace(/_/g, " ")}</span>
        <span className="tabular-nums text-white/40">{score}/{data?.max ?? "—"}</span>
      </div>
      <div className="h-1.5 rounded-full bg-white/8 overflow-hidden">
        <div className="h-full rounded-full bg-violet-400/80" style={{ width: `${pct}%` }} />
      </div>
      {data?.note ? <p className="mt-1 text-[9px] leading-snug text-white/35">{data.note}</p> : null}
    </div>
  );
}

function CtxChip({ label, value, tint }) {
  return (
    <div className="rounded-lg bg-white/5 px-2 py-1.5">
      <div className="text-[8px] uppercase tracking-wider text-white/35">{label}</div>
      <div className="text-[11px] font-semibold" style={{ color: tint || "#e4e4e7" }}>{value}</div>
    </div>
  );
}

/* ── expanded 5-layer breakdown ───────────────────────────────────────── */

function OracleDetail({ r }) {
  const layers = r.layers || {};
  const ctx = r.context || {};
  const kl = r.key_levels || {};
  const funding = Number(ctx.funding_rate || 0);
  return (
    <div className="mt-3 border-t border-white/8 pt-3 space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2.5">
        {["structure", "momentum", "order_flow", "key_levels", "context"].map((k) =>
          layers[k] ? <LayerBar key={k} label={k} data={layers[k]} /> : null
        )}
      </div>

      <div className="grid grid-cols-3 sm:grid-cols-5 gap-1.5">
        <CtxChip label="BTC Bias" value={ctx.btc_bias || "—"} tint={BIAS_STYLE[biasKind(ctx.btc_bias)].tint} />
        <CtxChip label="Funding" value={`${(funding * 100).toFixed(3)}%`} tint={funding >= 0 ? "#34d399" : "#fb7185"} />
        <CtxChip label="ADX" value={ctx.adx ? Number(ctx.adx).toFixed(1) : "—"} />
        <CtxChip label="Regime" value={ctx.regime || "—"} />
        <CtxChip label="Session" value={ctx.session || "—"} />
      </div>

      {(kl.support?.length || kl.resistance?.length || kl.poc) ? (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[10px]">
          {kl.poc ? <span className="text-white/45">POC <b className="text-white/70">{fmtPrice(kl.poc)}</b></span> : null}
          {kl.support?.length ? (
            <span className="text-emerald-300/70">Support {kl.support.slice(0, 3).map(fmtPrice).join(" · ")}</span>
          ) : null}
          {kl.resistance?.length ? (
            <span className="text-rose-300/70">Resistance {kl.resistance.slice(0, 3).map(fmtPrice).join(" · ")}</span>
          ) : null}
        </div>
      ) : null}

      {r.invalidation ? (
        <p className="text-[10px] text-amber-300/70">
          <span className="uppercase tracking-wide text-amber-300/50">Invalidation · </span>{r.invalidation}
        </p>
      ) : null}

      <DeepBlock deep={r.deep} />
      <PanelTake panel={r.panel} />
    </div>
  );
}

/* ── opportunity card ─────────────────────────────────────────────────── */

function OracleCard({ r, open, onToggle, calib }) {
  const k = biasKind(r.bias);
  const tint = BIAS_STYLE[k].tint;
  const hist = hitRateFor(calib, r.score, r.bias);
  const deep = r.deep;
  return (
    <div className="arena-equity-card cursor-pointer" onClick={onToggle}>
      <div className="flex items-center gap-3">
        <ScoreDial score={r.score} tint={tint} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="font-display text-sm font-bold text-white">{cleanSym(r.symbol)}</span>
            <BiasBadge bias={r.bias} />
            {hist != null ? (
              <span
                className="inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[9px] font-semibold tabular-nums"
                title="Historical hit-rate for this score band + direction (50% = coin-flip)"
                style={{ color: hitTint(hist), background: `${hitTint(hist)}18` }}
              >
                {hist}% hist
              </span>
            ) : null}
            {deep ? (
              <span
                className="inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[9px] font-semibold uppercase"
                title="Production quant gatekeeper verdict"
                style={{
                  color: deep.agrees ? "#34d399" : "#fb7185",
                  background: `${deep.agrees ? "#34d399" : "#fb7185"}18`,
                }}
              >
                {deep.agrees ? <CheckCircle2 className="h-2.5 w-2.5" /> : <XCircle className="h-2.5 w-2.5" />}
                Q{deep.quant_score != null ? ` ${deep.quant_score}` : ""}
              </span>
            ) : null}
          </div>
          <div className="mt-0.5 flex items-center gap-2 text-[11px] text-white/45">
            <span className="tabular-nums">${fmtPrice(r.price)}</span>
            {r.confidence ? <span className="text-white/30">·</span> : null}
            {r.confidence ? <span className="uppercase tracking-wide text-white/40">{r.confidence}</span> : null}
          </div>
          <div className="mt-1.5"><AlignDots tf={r.timeframe_alignment} /></div>
        </div>
      </div>
      {r.summary ? <p className="mt-2 text-[10px] leading-snug text-white/40 line-clamp-2">{r.summary}</p> : null}
      {open ? <OracleDetail r={r} /> : null}
    </div>
  );
}

/* ── calibration scoreboard ───────────────────────────────────────────── */

const VERDICT_STYLE = (v) => {
  const s = String(v || "").toLowerCase();
  if (s.includes("anti")) return { tint: "#fb7185", Icon: XCircle };
  if (s.includes("predictive") && !s.includes("no edge")) return { tint: "#34d399", Icon: CheckCircle2 };
  return { tint: "#fbbf24", Icon: AlertTriangle };
};

function CalibrationPanel({ calib }) {
  const [open, setOpen] = useState(false);
  if (!calib || !calib.bands || !calib.sample_size) return null;
  const { tint, Icon } = VERDICT_STYLE(calib.verdict);
  const bands = ["STRONG", "MODERATE", "WEAK", "NEUTRAL"];
  return (
    <section className="arena-equity-card">
      <button onClick={() => setOpen((o) => !o)} className="flex w-full items-center gap-2 text-left">
        <ScrollText className="h-4 w-4 text-cyan-300" />
        <span className="font-display text-[13px] font-semibold text-white">Score Calibration</span>
        <span
          className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold"
          style={{ color: tint, background: `${tint}18`, boxShadow: `inset 0 0 0 1px ${tint}44` }}
        >
          <Icon className="h-3 w-3" /> {calib.verdict}
        </span>
        <span className="ml-auto text-[9px] uppercase tracking-wider text-white/35">
          {calib.sample_size?.toLocaleString()} calls · {calib.horizon_hours}h fwd
        </span>
      </button>

      <p className="mt-2 text-[10px] leading-snug text-white/40">
        Does the score actually predict? Each band's real historical hit-rate on{" "}
        {calib.horizon_hours}h-forward price (50% = coin-flip). This is the honest track
        record behind every score below.
      </p>

      {open ? (
        <div className="mt-3 overflow-x-auto">
          <table className="w-full text-[10px]">
            <thead>
              <tr className="text-white/35 uppercase tracking-wider">
                <th className="text-left font-medium py-1">Band</th>
                <th className="text-right font-medium">Hit%</th>
                <th className="text-right font-medium">n</th>
                <th className="text-right font-medium">Bull hit%</th>
                <th className="text-right font-medium">Bear hit%</th>
                <th className="text-right font-medium">Med ret%</th>
              </tr>
            </thead>
            <tbody className="tabular-nums">
              {bands.map((b) => {
                const row = calib.bands[b];
                if (!row) return null;
                const c = row.combined || {};
                return (
                  <tr key={b} className="border-t border-white/6">
                    <td className="py-1 text-white/70">{b}</td>
                    <td className="text-right font-semibold" style={{ color: hitTint(c.hit) }}>
                      {c.hit ?? "—"}
                    </td>
                    <td className="text-right text-white/40">{c.n ?? 0}</td>
                    <td className="text-right" style={{ color: hitTint(row.BULL?.hit) }}>{row.BULL?.hit ?? "—"}</td>
                    <td className="text-right" style={{ color: hitTint(row.BEAR?.hit) }}>{row.BEAR?.hit ?? "—"}</td>
                    <td className="text-right text-white/45">{c.med_ret ?? "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : null}
    </section>
  );
}

/* ── deep-pass (production quant gatekeeper) ───────────────────────────── */

function DeepBlock({ deep }) {
  if (!deep) return null;
  const agrees = !!deep.agrees;
  const tint = agrees ? "#34d399" : "#fb7185";
  const AgIcon = agrees ? CheckCircle2 : XCircle;
  const lv = [
    ["Entry", deep.suggested_entry],
    ["Stop", deep.suggested_sl],
    ["TP1", deep.suggested_tp1],
    ["TP2", deep.suggested_tp2],
  ].filter(([, v]) => v);
  return (
    <div className="mt-3 rounded-xl bg-cyan-400/[0.05] p-2.5" style={{ boxShadow: "inset 0 0 0 1px rgba(34,211,238,0.18)" }}>
      <div className="flex items-center gap-2">
        <ShieldCheck className="h-3.5 w-3.5 text-cyan-300" />
        <span className="text-[10px] font-semibold uppercase tracking-wide text-cyan-200/90">Quant Gatekeeper</span>
        <span className="text-[9px] text-white/35">the analyzer AEON trades on</span>
        <span
          className="ml-auto inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[9px] font-semibold uppercase"
          style={{ color: tint, background: `${tint}18`, boxShadow: `inset 0 0 0 1px ${tint}44` }}
        >
          <AgIcon className="h-3 w-3" /> {agrees ? "Agrees" : "Disagrees"}
        </span>
      </div>
      <div className="mt-2 grid grid-cols-3 gap-1.5">
        <CtxChip label="Quant Score" value={deep.quant_score != null ? `${deep.quant_score}/100` : "—"} tint="#67e8f9" />
        <CtxChip label="Regime" value={deep.regime || "—"} />
        <CtxChip label="R:R" value={deep.r_r ? Number(deep.r_r).toFixed(2) : "—"} />
      </div>
      {lv.length ? (
        <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[10px]">
          {lv.map(([k, v]) => (
            <span key={k} className="text-white/45">{k} <b className="text-white/75">{fmtPrice(v)}</b></span>
          ))}
        </div>
      ) : null}
      {deep.anomalies?.length ? (
        <p className="mt-2 text-[10px] text-amber-300/70">
          <AlertTriangle className="mr-1 inline h-3 w-3" />
          {deep.anomalies.join(" · ")}
        </p>
      ) : null}
      {deep.reason ? <p className="mt-1.5 text-[9px] leading-snug text-white/35">{deep.reason}</p> : null}
    </div>
  );
}

function PanelTake({ panel }) {
  if (!panel?.take) return null;
  return (
    <div className="mt-3 rounded-xl bg-violet-400/[0.06] p-2.5" style={{ boxShadow: "inset 0 0 0 1px rgba(167,139,250,0.2)" }}>
      <div className="flex items-center gap-1.5">
        <Sparkles className="h-3.5 w-3.5 text-violet-300" />
        <span className="text-[10px] font-semibold uppercase tracking-wide text-violet-200/90">PhD Panel</span>
      </div>
      <p className="mt-1.5 text-[11px] italic leading-snug text-white/60">"{panel.take}"</p>
    </div>
  );
}

/* ── oracle summary (search narrative) ────────────────────────────────── */

const SUMMARY_VERDICT = (v) => {
  const s = String(v || "").toLowerCase();
  if (s.includes("action")) return { tint: "#34d399", label: "ACTIONABLE", Icon: CheckCircle2 };
  if (s.includes("avoid"))  return { tint: "#fb7185", label: "AVOID", Icon: XCircle };
  return { tint: "#fbbf24", label: "WATCH", Icon: AlertTriangle };
};

function OracleSummaryBlock({ summary }) {
  if (!summary || (!summary.narrative && !(summary.key_points?.length))) return null;
  const { tint, label, Icon } = SUMMARY_VERDICT(summary.verdict);
  const llm = summary.source && summary.source !== "computed";
  return (
    <div className="rounded-xl bg-cyan-400/[0.06] p-3" style={{ boxShadow: "inset 0 0 0 1px rgba(34,211,238,0.2)" }}>
      <div className="flex items-center gap-2">
        <MessageSquareQuote className="h-4 w-4 text-cyan-300" />
        <span className="font-display text-[12px] font-semibold text-white">Oracle Summary</span>
        <span
          className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[9px] font-semibold uppercase tracking-wide"
          style={{ color: tint, background: `${tint}18`, boxShadow: `inset 0 0 0 1px ${tint}44` }}
        >
          <Icon className="h-3 w-3" /> {label}
        </span>
        <span className="ml-auto text-[8px] uppercase tracking-wider text-white/30">
          {llm ? summary.source : "rule-based"}
        </span>
      </div>
      {summary.narrative ? (
        <p className="mt-2 text-[11px] leading-relaxed text-white/70">{summary.narrative}</p>
      ) : null}
      {summary.key_points?.length ? (
        <ul className="mt-2 space-y-1">
          {summary.key_points.map((p, i) => (
            <li key={i} className="flex gap-1.5 text-[10px] leading-snug text-white/45">
              <span className="mt-[3px] h-1 w-1 shrink-0 rounded-full bg-cyan-300/60" />
              {p}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

/* ── main panel ───────────────────────────────────────────────────────── */

const BIAS_FILTERS = ["ALL", "BULLISH", "BEARISH", "NEUTRAL"];

export default function OracleDeck() {
  const [report, setReport] = useState({ results: [], last_scan: null });
  const [entropy, setEntropy] = useState(null);
  const [calib, setCalib] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [minScore, setMinScore] = useState(60);
  const [biasFilter, setBiasFilter] = useState("ALL");
  const [openSym, setOpenSym] = useState(null);
  const [scan, setScan] = useState(null); // { in_progress, percent, scanned, total }

  // On-demand SEARCH: type any coin → full read (5-layer + quant gatekeeper + hist + summary).
  const [universe, setUniverse] = useState([]);
  const [query, setQuery] = useState("");
  const [searchResult, setSearchResult] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchErr, setSearchErr] = useState(null);
  const [searchOpen, setSearchOpen] = useState(true);

  useEffect(() => {
    axios.get(`${API}/oracle/universe`)
      .then(({ data }) => setUniverse(data?.coins || []))
      .catch(() => {});
  }, []);

  const runSearch = useCallback(async (raw) => {
    const sym = String(raw ?? query).trim().toUpperCase();
    if (!sym) return;
    setSearchLoading(true);
    setSearchErr(null);
    setSearchOpen(true);
    try {
      const { data } = await axios.get(`${API}/oracle/search/${encodeURIComponent(sym)}`);
      setSearchResult(data);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setSearchResult(null);
      setSearchErr(detail || `No Oracle read for “${sym}”`);
    } finally {
      setSearchLoading(false);
    }
  }, [query]);

  const load = useCallback(async () => {
    try {
      const [rep, ent, cal] = await Promise.allSettled([
        axios.get(`${API}/oracle/report`),
        axios.get(`${API}/oracle-entropy/status`),
        axios.get(`${API}/oracle/calibration`),
      ]);
      if (rep.status === "fulfilled") setReport(rep.value.data || { results: [] });
      if (ent.status === "fulfilled") setEntropy(ent.value.data);
      if (cal.status === "fulfilled") setCalib(cal.value.data);
      setErr(null);
    } catch (e) {
      setErr("Oracle feed unavailable");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  // Poll scan progress while a scan is running.
  useEffect(() => {
    if (!scan?.in_progress) return;
    const t = setInterval(async () => {
      try {
        const { data } = await axios.get(`${API}/oracle/scan/status`);
        setScan(data);
        if (!data?.in_progress) { clearInterval(t); load(); }
      } catch { clearInterval(t); }
    }, 4000);
    return () => clearInterval(t);
  }, [scan?.in_progress, load]);

  const runScan = async () => {
    try {
      await axios.get(`${API}/oracle/scan/full`);
      setScan({ in_progress: true, percent: 0, scanned: 0, total: 0 });
    } catch {
      setErr("Could not start scan");
    }
  };

  const rows = useMemo(() => {
    const all = report.results || [];
    return all
      .filter((r) => (Number(r.score) || 0) >= minScore)
      .filter((r) => biasFilter === "ALL" || biasKind(r.bias) === biasKind(biasFilter))
      .sort((a, b) => (Number(b.score) || 0) - (Number(a.score) || 0));
  }, [report, minScore, biasFilter]);

  const regime = entropy?.regime || "—";
  const Hm = Number(entropy?.H_market || 0);
  const regimeTint =
    /chaot/i.test(regime) ? "#fb7185" : /struct|trend/i.test(regime) ? "#34d399" : "#fbbf24";

  return (
    <div className="space-y-4">
      {/* HEADER */}
      <div className="flex items-center gap-2 px-1">
        <Eye className="h-4 w-4 text-cyan-300" />
        <h2 className="font-display text-base font-semibold text-white">Oracle</h2>
        <span className="ml-auto text-[9px] uppercase tracking-[0.22em] text-white/35">live · 30s</span>
      </div>

      {/* SEARCH — on-demand full read for any coin */}
      <section className="arena-equity-card">
        <form
          onSubmit={(e) => { e.preventDefault(); runSearch(); }}
          className="flex items-center gap-2"
        >
          <Search className="h-4 w-4 shrink-0 text-cyan-300" />
          <input
            list="oracle-universe"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Ask the Oracle about any coin — e.g. BTC, SOL, HYPE…"
            className="min-w-0 flex-1 bg-transparent text-[12px] text-white placeholder:text-white/30 focus:outline-none"
            autoComplete="off"
            spellCheck={false}
          />
          <datalist id="oracle-universe">
            {universe.map((c) => <option key={c} value={c} />)}
          </datalist>
          {searchResult || searchErr ? (
            <button
              type="button"
              onClick={() => { setQuery(""); setSearchResult(null); setSearchErr(null); }}
              className="rounded-lg px-2 py-1 text-[10px] font-semibold text-white/40 hover:text-white/70"
            >
              Clear
            </button>
          ) : null}
          <button
            type="submit"
            disabled={searchLoading || !query.trim()}
            className="flex items-center gap-1.5 rounded-xl bg-cyan-400/15 px-3 py-1.5 text-[11px] font-semibold text-cyan-200 hover:bg-cyan-400/25 disabled:opacity-40"
            style={{ boxShadow: "inset 0 0 0 1px rgba(34,211,238,0.35)" }}
          >
            {searchLoading
              ? <><Radar className="h-3.5 w-3.5 animate-pulse" /> Reading…</>
              : <><Eye className="h-3.5 w-3.5" /> Ask</>}
          </button>
        </form>

        {searchErr ? (
          <p className="mt-2 text-[11px] text-rose-300/80">{searchErr}</p>
        ) : null}

        {searchResult ? (
          <div className="mt-3 space-y-3">
            <OracleSummaryBlock summary={searchResult.oracle_summary} />
            <OracleCard
              r={searchResult}
              calib={calib}
              open={searchOpen}
              onToggle={() => setSearchOpen((o) => !o)}
            />
          </div>
        ) : null}
      </section>

      {/* MARKET STATE STRIP */}
      <section className="arena-equity-card">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <Activity className="h-3.5 w-3.5" style={{ color: regimeTint }} />
            <div>
              <div className="text-[8px] uppercase tracking-wider text-white/35">Market Regime</div>
              <div className="text-[13px] font-bold" style={{ color: regimeTint }}>{regime}</div>
            </div>
          </div>
          <div>
            <div className="text-[8px] uppercase tracking-wider text-white/35">Entropy Hₘ</div>
            <div className="text-[13px] font-bold tabular-nums text-white">{Hm ? Hm.toFixed(4) : "—"}</div>
          </div>
          <div>
            <div className="text-[8px] uppercase tracking-wider text-white/35">Scored Pairs</div>
            <div className="text-[13px] font-bold tabular-nums text-white">{report.results?.length || 0}</div>
          </div>
          <div>
            <div className="text-[8px] uppercase tracking-wider text-white/35">Last Scan</div>
            <div className="text-[13px] font-bold text-white">{fmtTime(report.last_scan)}</div>
          </div>

          <button
            onClick={runScan}
            disabled={scan?.in_progress}
            className="ml-auto flex items-center gap-1.5 rounded-xl bg-cyan-400/15 px-3 py-1.5 text-[11px] font-semibold text-cyan-200 hover:bg-cyan-400/25 disabled:opacity-50"
            style={{ boxShadow: "inset 0 0 0 1px rgba(34,211,238,0.35)" }}
          >
            <RefreshCw className={`h-3.5 w-3.5 ${scan?.in_progress ? "animate-spin" : ""}`} />
            {scan?.in_progress
              ? `Scanning ${scan?.scanned || 0}/${scan?.total || "…"}`
              : "Run Full Scan"}
          </button>
        </div>
        {scan?.in_progress ? (
          <div className="mt-3 h-1.5 rounded-full bg-white/8 overflow-hidden">
            <div className="h-full rounded-full bg-cyan-400/80 transition-all"
                 style={{ width: `${Math.max(2, Number(scan?.percent) || 0)}%` }} />
          </div>
        ) : null}
      </section>

      {/* CALIBRATION SCOREBOARD — the honest track record behind every score */}
      <CalibrationPanel calib={calib} />

      {/* FILTER ROW */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-1.5">
          {BIAS_FILTERS.map((b) => {
            const active = biasFilter === b;
            const tint = b === "ALL" ? "#e4e4e7" : BIAS_STYLE[biasKind(b)].tint;
            return (
              <button
                key={b}
                onClick={() => setBiasFilter(b)}
                className={`rounded-lg px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide transition-colors ${
                  active ? "text-white" : "text-white/40 hover:text-white/70"
                }`}
                style={active ? { background: `${tint}22`, boxShadow: `inset 0 0 0 1px ${tint}55`, color: tint } : undefined}
              >
                {b === "ALL" ? "All" : BIAS_STYLE[biasKind(b)].label}
              </button>
            );
          })}
        </div>

        <div className="ml-auto flex items-center gap-2">
          <Gauge className="h-3.5 w-3.5 text-white/40" />
          <span className="text-[10px] uppercase tracking-wide text-white/40">Min Score</span>
          <input
            type="range" min="0" max="100" step="5" value={minScore}
            onChange={(e) => setMinScore(Number(e.target.value))}
            className="w-28 accent-cyan-400"
          />
          <span className="w-6 text-[11px] font-bold tabular-nums text-white">{minScore}</span>
        </div>
      </div>

      {/* GRID */}
      {loading ? (
        <div className="flex items-center gap-2 px-1 py-8 text-white/40 text-[12px]">
          <Radar className="h-4 w-4 animate-pulse" /> Reading the tape…
        </div>
      ) : err ? (
        <div className="arena-equity-card text-[12px] text-rose-300/80">{err}</div>
      ) : rows.length === 0 ? (
        <div className="arena-equity-card flex flex-col items-center gap-2 py-8 text-center">
          <Layers className="h-5 w-5 text-white/25" />
          <p className="text-[12px] text-white/45">
            No pairs above score {minScore}
            {biasFilter !== "ALL" ? ` with ${BIAS_STYLE[biasKind(biasFilter)].label} bias` : ""}.
          </p>
          <p className="text-[10px] text-white/30">Lower the filter or run a fresh scan.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {rows.map((r) => (
            <OracleCard
              key={r.symbol}
              r={r}
              calib={calib}
              open={openSym === r.symbol}
              onToggle={() => setOpenSym(openSym === r.symbol ? null : r.symbol)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
